import React, { useEffect, useRef, useState } from 'react'
import { Button, InputNumber } from '@surecompanies/core_components'
import { Modal, message } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import {
  INVOICE_DELIVERY_QUERY_KEY,
  STATS_QUERY_KEY,
} from '../../api/invoice-delivery-api-client'
import { useCreateStripePaymentIntentMutation } from '../../api/stripe-payment-api-client'
import { InvoiceDeliveryItemDto } from '../../api/dto/invoice-delivery.dto'
import { formatCurrency } from './generation/utils'
import { dollarsToCents, resolveBankDetailLabel } from './paymentStatus'
import {
  AllocationErrorNote,
  AllocationFooterNote,
  AllocationStatus,
  DrawAmountLine,
  DrawClientName,
  DrawRowCard,
  DrawRowId,
  DrawRowInfo,
  DrawRowInputWrap,
  DrawRowList,
  DrawRowName,
  DrawWarningText,
  SplitSectionHeader,
  SplitSectionLabel,
} from './ConfirmDrawModal.styles'

const isFiniteAmount = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value)

// Floating-point cents can land a hair off an exact match (e.g. 61399.999999).
const ALLOCATION_TOLERANCE = 0.005

interface ConfirmDrawModalProps {
  delivery: InvoiceDeliveryItemDto | null
  open: boolean
  onClose: () => void
}

const ConfirmDrawModal: React.FC<ConfirmDrawModalProps> = ({
  delivery,
  open,
  onClose,
}) => {
  const queryClient = useQueryClient()
  const { mutateAsync } = useCreateStripePaymentIntentMutation()
  const [amounts, setAmounts] = useState<Record<string, number | null>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isSubmittingRef = useRef(false)

  const paymentList = delivery?.paymentDetail?.paymentList ?? []
  const totalDue = delivery?.invoiceMetadata?.totalDue

  useEffect(() => {
    if (!open) return
    setAmounts({})
    isSubmittingRef.current = false
    setIsSubmitting(false)
  }, [open, delivery?.id])

  if (!delivery) return null

  const allocatedTotal = paymentList.reduce((sum, attempt) => {
    const key = attempt.stripeCustomerId ?? ''
    return sum + (amounts[key] ?? 0)
  }, 0)

  const isFullyAllocated =
    isFiniteAmount(totalDue) &&
    paymentList.length > 0 &&
    Math.abs(allocatedTotal - totalDue) < ALLOCATION_TOLERANCE

  const handleConfirm = async () => {
    if (!isFullyAllocated || isSubmittingRef.current) return
    isSubmittingRef.current = true
    setIsSubmitting(true)

    // A $0 allocation draws nothing, so skip it rather than firing a no-op
    // payment intent against that customer.
    const targets = paymentList.filter(
      (attempt): attempt is typeof attempt & { stripeCustomerId: string } =>
        !!attempt.stripeCustomerId &&
        (amounts[attempt.stripeCustomerId] ?? 0) > 0
    )

    try {
      const results = await Promise.allSettled(
        targets.map((attempt) =>
          mutateAsync({
            stripeCustomerId: attempt.stripeCustomerId,
            body: {
              amount: dollarsToCents(amounts[attempt.stripeCustomerId] ?? 0),
              billingPeriod: delivery.billingPeriodStart,
            },
          })
        )
      )

      const failed = results
        .map((result, index) => ({ result, attempt: targets[index] }))
        .filter(({ result }) => result.status === 'rejected')

      if (failed.length > 0) {
        const labels = failed
          .map(
            ({ attempt }) =>
              resolveBankDetailLabel(attempt) ?? attempt.stripeCustomerId
          )
          .join(', ')
        message.error(
          `Draw failed for: ${labels}. Amounts already drawn were not reversed — reconcile before retrying.`
        )
      } else {
        message.success('Draw confirmed.')
      }

      // Always refresh and close. A mixed batch has already charged some
      // customers; leaving Confirm enabled on the stale snapshot would let a
      // retry draw those amounts again.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [INVOICE_DELIVERY_QUERY_KEY],
        }),
        queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] }),
      ])
      onClose()
    } finally {
      isSubmittingRef.current = false
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      title="Confirm draw"
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          disabled={!isFullyAllocated || isSubmitting}
          loading={isSubmitting}
        >
          Confirm
        </Button>,
      ]}
      width={520}
      destroyOnClose
    >
      <DrawClientName>
        {delivery.clientName ?? 'Unmatched Company'}
      </DrawClientName>
      <DrawAmountLine>
        Amount: {isFiniteAmount(totalDue) ? formatCurrency(totalDue) : '—'}
      </DrawAmountLine>
      <DrawWarningText>
        This will draw funds from the system. This action cannot be undone.
      </DrawWarningText>

      <SplitSectionHeader>
        <SplitSectionLabel>Split Across Sub-Companies</SplitSectionLabel>
        {isFiniteAmount(totalDue) && paymentList.length > 0 && (
          <AllocationStatus $ok={isFullyAllocated}>
            {isFullyAllocated
              ? 'Fully allocated'
              : `Off by ${formatCurrency(Math.abs(allocatedTotal - totalDue))}`}
          </AllocationStatus>
        )}
      </SplitSectionHeader>

      {paymentList.length === 0 ? (
        <AllocationErrorNote>
          No sub-company payment methods on file yet.
        </AllocationErrorNote>
      ) : (
        <DrawRowList>
          {paymentList.map((attempt, index) => {
            const key = attempt.stripeCustomerId ?? `row-${index}`
            const bankDetailLabel = resolveBankDetailLabel(attempt)
            return (
              <DrawRowCard key={key}>
                <DrawRowInfo>
                  {attempt.stripeBillingProfileDescription && (
                    <DrawRowName>
                      {attempt.stripeBillingProfileDescription}
                    </DrawRowName>
                  )}
                  <DrawRowName $isWarning={!bankDetailLabel}>
                    {bankDetailLabel ?? 'No payment method'}
                  </DrawRowName>
                  {attempt.stripeCustomerId && (
                    <DrawRowId>
                      {attempt.stripeCustomerId}
                      {attempt.stripeAccount && ` (${attempt.stripeAccount})`}
                    </DrawRowId>
                  )}
                </DrawRowInfo>
                <DrawRowInputWrap>
                  <InputNumber
                    value={amounts[key] ?? undefined}
                    onChange={(v) =>
                      setAmounts((prev) => ({
                        ...prev,
                        [key]: typeof v === 'number' ? v : null,
                      }))
                    }
                    placeholder="0.00"
                    min={0}
                    precision={2}
                    step={0.01}
                    prefix="$"
                    style={{ width: '100%' }}
                    disabled={
                      isSubmitting ||
                      !attempt.stripeCustomerId ||
                      !bankDetailLabel
                    }
                  />
                </DrawRowInputWrap>
              </DrawRowCard>
            )
          })}
        </DrawRowList>
      )}

      {isFiniteAmount(totalDue) && paymentList.length > 0 && (
        <AllocationFooterNote>
          Allocated {formatCurrency(allocatedTotal)} of{' '}
          {formatCurrency(totalDue)}
        </AllocationFooterNote>
      )}
      {!isFullyAllocated && paymentList.length > 0 && (
        <AllocationErrorNote>
          {isFiniteAmount(totalDue)
            ? 'Amounts must add up to the invoice total before you can draw.'
            : 'This invoice has no readable Total Due to draw against.'}
        </AllocationErrorNote>
      )}
    </Modal>
  )
}

export { ConfirmDrawModal }
